import { useState } from "react";
import { ModifierGroup } from "../types";
import useDebounce from "../hooks/useDebounce";
import {
  useDeleteModifierGroupMutation,
  useFetchModifierGroupsQuery,
  useTypedSelector,
} from "../store";
import { showToast, ToastType } from "../utils/toast.utils";
import Pagination from "../components/common/Pagination";
import Modal from "../components/common/Modal";
import Button from "../components/common/Button";
import { FaEdit, FaRegTrashAlt, FaPlus, FaSearch } from "react-icons/fa";
import { Link } from "react-router-dom";

const ModifierGroupsWrapper = () => {
  const restaurant = useTypedSelector((state) => state.session.restaurant);

  if (!restaurant)
    return (
      <div className="w-full flex justify-center mt-4 text-xl font-semibold text-gray-500">
        Veuillez sélectionner un restaurant
      </div>
    );

  return (
    <div className="w-full p-6">
      <ModifierGroups restaurantId={restaurant._id} />
    </div>
  );
};

interface ModifierGroupsProps {
  restaurantId: string;
}

const ModifierGroups: React.FC<ModifierGroupsProps> = ({ restaurantId }) => {
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [displayName, setDisplayName] = useState<string>("");
  const debouncedDislayName = useDebounce(displayName, 400);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modifierToDelete, setModifierToDelete] =
    useState<ModifierGroup | null>(null);

  const [deleteModifierGroup] = useDeleteModifierGroupMutation();

  const confirmDelete = (modifierToDelete: ModifierGroup) => {
    setModifierToDelete(modifierToDelete);
    setShowConfirmModal(true);
  };

  const handleDeleteModifierGroup = async () => {
    if (!modifierToDelete) return;
    const result = await deleteModifierGroup(modifierToDelete._id);
    if (!result.error) {
      showToast("Groupe de supplément supprimé", ToastType.SUCCESS);
    }
    setShowConfirmModal(false);
    setModifierToDelete(null);
  };

  const { data: modifierGroups, isLoading } = useFetchModifierGroupsQuery({
    restaurantId,
    searchQuery: {
      page,
      perPage,
      displayName: debouncedDislayName,
    },
  });

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        {/* Header Section */}
        <div className="p-6 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-4">
          <h2 className="text-xl font-bold text-gray-800">
            Groupes de Suppléments
          </h2>

          <div className="flex items-center gap-4 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-64">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                <FaSearch />
              </span>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Rechercher..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
              />
            </div>

            <Link
              to="/modifier-groups/create"
              className="flex items-center gap-2 bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              <FaPlus />
              <span>Ajouter</span>
            </Link>
          </div>
        </div>

        {/* Table Section */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 uppercase text-xs tracking-wider">
                <th className="px-6 py-4 font-semibold">Nom du Groupe</th>
                <th className="px-6 py-4 font-semibold text-center">Options</th>
                <th className="px-6 py-4 font-semibold text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td
                    colSpan={3}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    Chargement...
                  </td>
                </tr>
              ) : modifierGroups?.data?.length === 0 ? (
                <tr>
                  <td
                    colSpan={3}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    Aucun groupe trouvé
                  </td>
                </tr>
              ) : (
                modifierGroups?.data?.map((row) => (
                  <tr
                    key={row._id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 text-gray-800 font-medium">
                      {row.displayName}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="bg-blue-50 text-blue-700 py-1 px-3 rounded-full text-xs font-semibold">
                        {row.modifierOptions?.length || 0} options
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center items-center gap-3">
                        <Link
                          to={row._id}
                          className="text-gray-400 hover:text-primary transition-colors p-2 rounded-full hover:bg-blue-50"
                          title="Modifier"
                        >
                          <FaEdit size={18} />
                        </Link>
                        <button
                          onClick={() => confirmDelete(row)}
                          className="text-gray-400 hover:text-red-500 transition-colors p-2 rounded-full hover:bg-red-50"
                          title="Supprimer"
                        >
                          <FaRegTrashAlt size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Section */}
        <div className="p-4 border-t border-gray-200">
          <Pagination
            paginationInfo={{
              page,
              perPage,
              count: modifierGroups?.paginationInfo.count || 1,
            }}
            setPage={setPage}
            setPerPage={setPerPage}
          />
        </div>
      </div>

      {showConfirmModal && (
        <Modal
          onClose={() => setShowConfirmModal(false)}
          actionBar={
            <div className="flex justify-end gap-3 w-full">
              <Button
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium transition-colors"
                onClick={() => setShowConfirmModal(false)}
              >
                Annuler
              </Button>
              <Button
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                onClick={handleDeleteModifierGroup}
              >
                Supprimer
              </Button>
            </div>
          }
        >
          <div className="text-center p-4">
            <h3 className="text-lg font-bold text-gray-800 mb-2">
              Confirmer la suppression
            </h3>
            <p className="text-gray-600">
              Êtes-vous sûr de vouloir supprimer le groupe{" "}
              <span className="font-semibold text-gray-900">
                {modifierToDelete?.displayName}
              </span>{" "}
              ?
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ModifierGroupsWrapper;
